import { z } from 'zod'
import { UserFileReadSchema } from '@/uppy-file-upload/schemas'
import { FolderResponseSchema } from '@/user_files/folders/schemas'

// ============================================================================
// Response Schemas (Zod - runtime validated)
// ============================================================================

/**
 * One root-relative breadcrumb entry.
 *
 * The backend excludes the matter root from `folder_path`, and for a folder result it also
 * excludes the folder itself, so the last entry is always the item's immediate parent folder.
 * A top-level folder — and a file sitting directly in the matter root — comes back with an
 * empty array.
 */
export const FolderPathEntrySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
})

export type FolderPathEntry = z.infer<typeof FolderPathEntrySchema>

/**
 * FolderSearchResult - a matching folder plus its ancestor breadcrumbs.
 * Used by: GET /api/v1/matter/{matter_id}/folders/search
 */
export const FolderSearchResultSchema = FolderResponseSchema.extend({
  folder_path: z.array(FolderPathEntrySchema),
})

export type FolderSearchResult = z.infer<typeof FolderSearchResultSchema>

export const FolderSearchResponseSchema = z.object({
  matter_id: z.string().uuid(),
  folders: z.array(FolderSearchResultSchema),
  next_cursor: z.string().nullable(),
  /** Total matching folders before cursor pagination. */
  total_count: z.number().int().nonnegative(),
})

export type FolderSearchResponse = z.infer<typeof FolderSearchResponseSchema>

/**
 * FileSearchResult - a matching file plus its containing-folder breadcrumbs.
 * Used by: GET /api/v1/matter/{matter_id}/files/search
 */
export const FileSearchResultSchema = UserFileReadSchema.extend({
  folder_path: z.array(FolderPathEntrySchema),
})

export type FileSearchResult = z.infer<typeof FileSearchResultSchema>

export const FileSearchResponseSchema = z.object({
  matter_id: z.string().uuid(),
  files: z.array(FileSearchResultSchema),
  next_cursor: z.string().nullable(),
  /** Total matching files before cursor pagination. */
  total_count: z.number().int().nonnegative(),
})

export type FileSearchResponse = z.infer<typeof FileSearchResponseSchema>
